import React, {useEffect, useState} from 'react';
import {deriveOfferDisplay} from './offerDisplay';
import PropTypes from 'prop-types';
import moment from 'moment';
import {FormattedMessage, useIntl} from 'react-intl';
import SingleOffer from 'wobi-web-common/dist/components/Offer';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {CircularProgress, Collapse, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {makeStyles} from 'tss-react/mui';
import {fireGtmEvent} from '../../utils/marketing.utils';
import logos from '../../assets/images/company-logos';
import {destinationsToNames} from '../../utils/routingHelpers';
import {ridersTypes} from '../../config/ridersTypes';
import {TEST_IDS} from '../../config/testIds';
import {Breakpoints} from 'wobi-web-common';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      paddingBottom: 76,
    },
  },
  summaryBand: {
    width: '100%',
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      overflowX: 'visible',
      overscrollBehaviorInline: 'contain',
    },
  },
  summaryInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
    flexWrap: 'nowrap',
    minHeight: 58,
    paddingBlock: 10,
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      alignItems: 'stretch',
      flexDirection: 'column',
      gap: 0,
      width: '100%',
      minWidth: '100%',
      paddingBlock: 10,
    },
  },
  desktopSummaryContent: {
    display: 'contents',
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      display: 'none',
    },
  },
  mobileSummary: {
    display: 'none',
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      display: 'block',
      width: '100%',
    },
  },
  mobileSummaryActions: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: 10,
  },
  mobileSummaryButton: {
    minWidth: 0,
    height: 44,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    padding: '0 12px',
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.secondary.dark,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: theme.typography.fontWeightBold,
    whiteSpace: 'nowrap',
  },
  mobileCoverageButton: {
    position: 'fixed',
    insetInline: 16,
    bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    zIndex: 10,
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 5px 14px ${alpha(theme.palette.primary.main, 0.24)}`,
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
  mobileCoverageBadge: {
    minWidth: 22,
    height: 22,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingInline: 6,
    borderRadius: 9999,
    background: alpha(theme.palette.primary.contrastText, 0.24),
    color: theme.palette.primary.contrastText,
    fontSize: 12,
    fontWeight: theme.typography.fontWeightBold,
  },
  mobileSummaryChevron: {
    display: 'inline-flex',
    color: theme.palette.text.secondary,
    transition: 'transform 180ms ease-out',
  },
  mobileSummaryChevronOpen: {
    transform: 'rotate(180deg)',
  },
  mobileCriteriaPanel: {
    paddingTop: 12,
  },
  mobileCriteriaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  mobileCriteriaTag: {
    minWidth: 0,
    width: '100%',
    paddingInline: 10,
    '& $criteriaTagValue': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  mobileCoveragesTag: {
    minHeight: 42,
    height: 'auto',
    display: 'block',
    padding: 0,
    overflow: 'hidden',
  },
  mobileCoveragesToggle: {
    width: '100%',
    minHeight: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '0 10px',
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  mobileCoveragesSummary: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    overflow: 'hidden',
  },
  mobileCoveragesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    margin: 0,
    padding: '0 10px 10px',
    listStyle: 'none',
    textAlign: 'right',
  },
  mobileCoverageListItem: {
    position: 'relative',
    paddingInlineStart: 12,
    color: theme.palette.text.primary,
    fontSize: 12.5,
    fontWeight: theme.typography.fontWeightSemiBold,
    lineHeight: 1.35,
    whiteSpace: 'normal',
    '&::before': {
      position: 'absolute',
      insetInlineStart: 0,
      top: '.55em',
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: theme.palette.primary.main,
      content: '""',
    },
  },
  mobileEditRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: 4,
  },
  criteriaBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
    flexWrap: 'nowrap',
    flex: 'none',
    textAlign: 'start',
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      gap: 8,
    },
  },
  criteriaTag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 42,
    padding: '0 14px',
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    fontFamily: 'inherit',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    flex: 'none',
  },
  criteriaTagLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  criteriaTagValue: {
    fontSize: 13.5,
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  editLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13.5,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.secondary.dark,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    minHeight: 42,
    padding: '0 4px',
    flex: 'none',
  },
  pageIntro: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingBlock: 'clamp(20px, 3vw, 32px) 20px',
  },
  // Sort pills
  sortContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 16,
  },
  sortPills: {
    display: 'inline-flex',
    background: theme.palette.action.selected,
    borderRadius: 9999,
    padding: 4,
    gap: 2,
  },
  sortPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    cursor: 'pointer',
    minWidth: 112,
    height: 34,
    padding: '0 16px',
    borderRadius: 9999,
    fontWeight: 700,
    fontSize: 13.5,
    whiteSpace: 'nowrap',
    transition: 'all .2s',
    border: '1.5px solid transparent',
    background: 'transparent',
    color: theme.palette.text.secondary,
    boxShadow: 'none',
    fontFamily: 'inherit',
  },
  sortPillActive: {
    background: theme.palette.background.paper,
    color: theme.palette.secondary.main,
    border: `1.5px solid ${theme.palette.secondary.main}`,
    boxShadow: `0 1px 5px ${alpha(theme.palette.secondary.main, 0.16)}`,
  },
  sortTooltip: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    maxWidth: 520,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    boxShadow: theme.shadows[1],
    padding: '12px 14px',
    marginTop: 12,
    fontSize: 12.5,
    lineHeight: 1.55,
    color: theme.palette.text.secondary,
    textAlign: 'right',
  },
  // Offer card overrides
  offerWrapper: {
    '& [name="offerContentContainer"]': {
      borderRadius: '16px !important',
      border: `1px solid ${theme.palette.divider} !important`,
      boxShadow: `${theme.shadows[1]} !important`,
      overflow: 'hidden',
    },
    '&.rank-1 [name="offerContentContainer"]': {
      border: `1.5px solid ${theme.palette.secondary.main} !important`,
      boxShadow: `0 10px 28px ${alpha(theme.palette.secondary.main, 0.12)} !important`,
    },
    // Override SingleOffer footer toggle button
    '& .offer-footer': {
      padding: '4px 22px 14px !important',
      borderTop: 'none !important',
      minHeight: 'auto !important',
      [theme.breakpoints.down(Breakpoints.mobile)]: {
        direction: 'ltr',
        display: 'grid !important',
        justifyItems: 'end',
        padding: '4px 18px 14px !important',
        textAlign: 'right',
        width: '100%',
      },
    },
    '& .offer-footer button': {
      padding: '0 !important',
      background: 'none !important',
      border: 'none !important',
      cursor: 'pointer',
      color: `${theme.palette.secondary.dark} !important`,
      fontWeight: '700 !important',
      fontSize: '13.5px !important',
      display: 'inline-flex !important',
      alignItems: 'center',
      gap: '6px !important',
      boxShadow: 'none !important',
      margin: '0 !important',
      width: 'auto !important',
      [theme.breakpoints.down(Breakpoints.mobile)]: {
        direction: 'rtl',
        justifySelf: 'end',
      },
    },
    '& .offer-footer button:hover': {
      background: 'none !important',
      textDecoration: 'underline',
    },
    '& .offer-footer button .info': {
      color: `${theme.palette.secondary.dark} !important`,
    },
    '& .offer-footer button img': {
      display: 'none !important',
    },
    // An SVG mask keeps the chevron vertical without RTL border mirroring.
    '& .offer-footer button::after': {
      content: '""',
      width: 12,
      height: 8,
      flexShrink: 0,
      backgroundColor: 'currentColor',
      maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 12 8\'%3E%3Cpath d=\'M1 1L6 6L11 1\' fill=\'none\' stroke=\'black\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
      maskRepeat: 'no-repeat',
      maskSize: 'contain',
      transform: 'rotate(0deg)',
      transition: 'transform 180ms ease-out',
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
    '& .offer-footer button:has(img.rotate)::after': {
      transform: 'rotate(180deg)',
    },
    // Override SingleOffer covers expansion section
    '& .MuiCollapse-root > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner > div': {
      padding: '16px 22px 20px !important',
      borderTop: `1px dashed ${theme.palette.divider} !important`,
      background: `${theme.palette.action.selected} !important`,
      maxWidth: '100% !important',
      width: '100% !important',
    },
    '& .table-title': {
      fontSize: '13px !important',
      fontWeight: '700 !important',
      color: `${theme.palette.text.primary} !important`,
      marginBottom: '12px !important',
      marginTop: '0 !important',
    },
    '& .covers-table': {
      background: `${theme.palette.background.paper} !important`,
      border: `1px solid ${theme.palette.divider} !important`,
      borderRadius: '8px !important',
      overflow: 'hidden',
      marginBottom: '20px !important',
    },
    '& .covers-table table': {
      width: '100%',
      borderCollapse: 'collapse',
    },
    '& .covers-table th': {
      textAlign: 'right',
      padding: '10px 16px !important',
      fontSize: '11.5px !important',
      fontWeight: '700 !important',
      color: `${theme.palette.text.secondary} !important`,
      background: `${theme.palette.action.selected} !important`,
      borderBottom: `1px solid ${theme.palette.divider} !important`,
    },
    '& .covers-table td': {
      padding: '11px 16px !important',
      fontSize: '14px !important',
      fontWeight: '600 !important',
      color: `${theme.palette.text.primary} !important`,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .covers-table tr:last-child td': {
      borderBottom: 'none',
    },
    '& .pdf-button': {
      borderRadius: '8px !important',
      fontSize: '13px !important',
    },
    // Hide the left additionalPrices column (fallback if noLeft prop doesn't work)
    '& .wrapper': {
      display: 'block !important',
    },
    // Force right section to full width since noLeft hides the left column
    '& .wrapper > div:first-child': {
      width: '100% !important',
    },
  },
  // Offer children grid
  offerGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 170px auto',
    alignItems: 'center',
    columnGap: 18,
    rowGap: 14,
    padding: '20px 22px 6px',
    [theme.breakpoints.down(900)]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      direction: 'ltr',
      gridTemplateAreas: `
        'header header'
        'stats stats'
        'info info'
        'buy buy'
      `,
      gridTemplateColumns: '1fr 1fr',
      columnGap: 12,
      rowGap: 16,
      padding: '16px 14px 8px',
    },
  },
  mobileHeader: {
    display: 'none',
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row-reverse',
      gridArea: 'header',
      justifyContent: 'space-between',
      paddingInline: 8,
      width: '100%',
    },
  },
  mobileLogoContainer: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  mobilePriceBlock: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  offerIdColumn: {
    display: 'flex',
    gap: 18,
    alignItems: 'flex-start',
    minWidth: 0,
    [theme.breakpoints.down(900)]: {
      gridColumn: '1 / -1',
    },
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      display: 'contents',
    },
  },
  logoContainer: {
    flex: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      display: 'none',
    },
  },
  logo: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 108,
    height: 52,
    overflow: 'hidden',
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
  },
  cheapestBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: theme.palette.action.selected,
    color: theme.palette.secondary.dark,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 9999,
    padding: '3px 11px',
    fontWeight: 700,
    fontSize: 11,
    whiteSpace: 'nowrap',
  },
  importantInfoSection: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    textAlign: 'start',
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      gridArea: 'info',
      direction: 'rtl',
      textAlign: 'right',
    },
  },
  importantInfoTitle: {
    width: '100%',
    textAlign: 'start',
    fontWeight: 800,
    fontSize: 15,
    color: theme.palette.primary.main,
    marginBottom: 8,
  },
  importantInfoList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    width: '100%',
    textAlign: 'start',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  importantInfoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    direction: 'rtl',
    textAlign: 'right',
    gap: 8,
    fontSize: 13.5,
    color: theme.palette.text.primary,
    lineHeight: 1.45,
  },
  importantInfoDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    flex: 'none',
    marginTop: 7,
  },
  // Stat boxes
  statsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      direction: 'rtl',
      flexDirection: 'row',
      gridArea: 'stats',
      width: '100%',
    },
  },
  statBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflow: 'hidden',
    textAlign: 'center',
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  statBoxHeader: {
    background: '#ecf9fe',
    color: '#236a92',
    fontWeight: 700,
    fontSize: 12,
    padding: '6px 8px',
  },
  statBoxValue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: 13.5,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  // Price + buy
  priceColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    [theme.breakpoints.down(900)]: {
      gridColumn: '1 / -1',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      display: 'contents',
    },
  },
  priceBlock: {
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      display: 'none',
    },
  },
  priceValue: {
    fontWeight: 800,
    fontSize: 30,
    color: theme.palette.text.primary,
    lineHeight: 1,
    direction: 'ltr',
    textAlign: 'center',
  },
  priceSubtext: {
    fontSize: 11.5,
    color: theme.palette.text.secondary,
    marginTop: 5,
    textAlign: 'center',
  },
  buyActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    [theme.breakpoints.down(900)]: {
      width: 'auto',
    },
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      gridArea: 'buy',
      width: '100%',
      direction: 'rtl',
    },
  },
  pendingChangesMessage: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.3,
    maxWidth: 180,
    textAlign: 'center',
  },
  buyButton: {
    width: '100%',
    height: 46,
    padding: '0 30px',
    border: 'none',
    cursor: 'pointer',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 9999,
    fontFamily: 'inherit',
    fontWeight: 700,
    fontSize: 16,
    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.28)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    [theme.breakpoints.down(900)]: {
      width: 'auto',
      minWidth: 130,
    },
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  pageTitle: {
    fontWeight: 800,
    fontSize: 'clamp(26px, 2.8vw, 34px)',
    lineHeight: 1.2,
    color: theme.palette.secondary.main,
    textAlign: 'start',
    margin: '0 0 6px',
  },
  titleDot: {
    display: 'inline-block',
    width: '.24em',
    height: '.24em',
    borderRadius: '50%',
    background: theme.palette.primary.main,
    marginInlineStart: '.08em',
    verticalAlign: 'baseline',
  },
  pageLead: {
    fontSize: 14.5,
    color: theme.palette.text.secondary,
    textAlign: 'start',
    margin: '0 0 20px',
  },
  highlight: {
    color: theme.palette.primary.main,
  },
  loadingIndicator: {
    color: theme.palette.primary.contrastText,
  },
}));

const OffersContainer = ({
  offers,
  submitAct,
  isSubmitting,
  hasPendingRiderChanges,
}) => {
  const intl = useIntl();
  const {classes, cx} = useStyles();
  const isPregnancy = JSON.parse(sessionStorage.getItem('travel.details-form')).values.isPregnancy;
  const [chosenOffersIndex, setChosenOferIndex] = useState(null);
  const [typeOfSortOffers, setTypeOfSortOffers] = useState('mostCheap');
  const [isShowingSorting, setIsShowingSorting] = useState(false);
  const [sortNoteOpen, setSortNoteOpen] = useState(false);

  useEffect(() => {
    let hasSorting = false;
    offers.forEach((offer) => {
      if (offer.popularityScore) {
        hasSorting = true;
      }
    });
    setIsShowingSorting(hasSorting);
  }, [offers]);

  const handleTypeSortOffer = (type) => {
    fireGtmEvent('TravelSortBy', {sortBy: type});
    setTypeOfSortOffers(type);
    if (type !== 'mostPopular') {
      setSortNoteOpen(false);
    }
  };

  const sortedOfferKeys = Object.keys(offers).sort((a, b) => {
    const offerA = offers[a];
    const offerB = offers[b];
    if (typeOfSortOffers === 'mostCheap') {
      return offerA.price - offerB.price;
    }
    if (typeOfSortOffers === 'mostPopular') {
      return offerB.popularityScore - offerA.popularityScore;
    }
    return 0;
  });

  const values = JSON.parse(sessionStorage.getItem('travel.details-form')).values;
  const getCoverageValue = (offer) => {
    const item = offer.additionalPrices && offer.additionalPrices.find((el) => el.id === 2);
    if (!item) return '—';
    return typeof item.value === 'number' ? `$${item.value.toLocaleString()}` : item.value;
  };

  const getDeductibleValue = (offer) => {
    const item = offer.additionalPrices && offer.additionalPrices.find((el) => el.id === 3);
    if (!item) return '—';
    return typeof item.value === 'number' ? `$${item.value.toLocaleString()}` : item.value;
  };

  return (
    <div className={classes.root}>
      {/* Sort pills */}
      {isShowingSorting && (
        <div className={classes.sortContainer}>
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <div className={classes.sortPills}>
              <button
                className={cx(classes.sortPill, typeOfSortOffers === 'mostCheap' && classes.sortPillActive)}
                onClick={() => handleTypeSortOffer('mostCheap')}
              >
                <FormattedMessage id='offers.list.mostCheapTab'/>
              </button>
              <button
                className={cx(classes.sortPill, typeOfSortOffers === 'mostPopular' && classes.sortPillActive)}
                onClick={() => {
                  handleTypeSortOffer('mostPopular');
                  setSortNoteOpen((v) => !v);
                }}
              >
                <FormattedMessage id='offers.list.mostPopularTab'/>
              </button>
            </div>
            {sortNoteOpen && typeOfSortOffers === 'mostPopular' && (
              <div className={classes.sortTooltip}>
                <FormattedMessage id='offers.list.mostPopularTabTooltip'/>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Offer cards */}
      {sortedOfferKeys.map((offerKey, index) => {
        const offer = deriveOfferDisplay(offers[offerKey], {
          isPregnancy,
          isEsports: values.isEsports,
          isWsports: values.isWsports,
        }, intl.formatMessage);
        const rank = index + 1;

        return (
          <div
            key={`offerNo-${offerKey}`}
            data-testid={TEST_IDS.offers.row}
            data-provider={offer.provider}
            data-price={Math.ceil(offer.price)}
            className={cx(classes.offerWrapper, rank === 1 && 'rank-1')}
          >
            <SingleOffer
              data={{
                ...offer,
                pdfLink: `/policies/${offer.productLineId}/policy.pdf`,
              }}
              noLeft
              currency='empty'
              isOpen
              showProtectionTitle={false}
              extraPrimaryCovers
              shouldFilterEmptyCovers
              expandCoversHandler={() => fireGtmEvent('showMore', {provider: offer.provider})}
            >
              <div className={classes.offerGrid}>
                <div className={classes.mobileHeader}>
                  <div className={classes.mobilePriceBlock}>
                    <div className={classes.priceValue}>${Math.ceil(offer.price)}</div>
                    <div className={classes.priceSubtext}><FormattedMessage id='offers.summary.entireTrip'/></div>
                  </div>
                  <div className={classes.mobileLogoContainer}>
                    {rank === 1 && (
                      <span className={classes.cheapestBadge}><FormattedMessage id='offers.summary.cheapest'/></span>
                    )}
                    <div className={classes.logo}>
                      <img src={logos[offer.logo]} alt={offer.productLineName || ''}/>
                    </div>
                  </div>
                </div>

                {/* Logo + important info */}
                <div className={classes.offerIdColumn}>
                  <div className={classes.logoContainer}>
                    {rank === 1 && (
                      <span className={classes.cheapestBadge}><FormattedMessage id='offers.summary.cheapest'/></span>
                    )}
                    <div className={classes.logo}>
                      <img src={logos[offer.logo]} alt={offer.productLineName || ''}/>
                    </div>
                  </div>
                  <div className={classes.importantInfoSection} dir='rtl' style={{direction: 'rtl', textAlign: 'right'}}>
                    {offer.importantInfo && (
                      <>
                        <div className={classes.importantInfoTitle} dir='rtl' style={{direction: 'rtl', textAlign: 'right'}}><FormattedMessage id='offers.summary.importantInfo'/></div>
                        <ul className={classes.importantInfoList} dir='rtl' style={{direction: 'rtl', textAlign: 'right'}}>
                          {offer.importantInfo.map((item, i) => (
                            <li key={i} className={classes.importantInfoItem} dir='rtl' style={{direction: 'rtl', textAlign: 'right'}}>
                              <span className={classes.importantInfoDot}/>
                              <span dir='rtl' style={{flex: 1, direction: 'rtl', textAlign: 'right'}}>{item.text}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>

                {/* Stat boxes */}
                <div className={classes.statsColumn}>
                  <div className={classes.statBox}>
                    <div className={classes.statBoxHeader}><FormattedMessage id='offers.summary.coverage'/></div>
                    <div className={classes.statBoxValue}>{getCoverageValue(offer)}</div>
                  </div>
                  <div className={classes.statBox}>
                    <div className={classes.statBoxHeader}><FormattedMessage id='offers.summary.deductible'/></div>
                    <div className={classes.statBoxValue}>{getDeductibleValue(offer)}</div>
                  </div>
                </div>

                {/* Price + buy button */}
                <div className={classes.priceColumn}>
                  <div className={classes.priceBlock}>
                    <div className={classes.priceValue}>${Math.ceil(offer.price)}</div>
                    <div className={classes.priceSubtext}><FormattedMessage id='offers.summary.entireTrip'/></div>
                  </div>
                  <div className={classes.buyActions}>
                    <button
                      className={classes.buyButton}
                      onClick={() => {
                        submitAct(offerKey);
                        setChosenOferIndex(offerKey);
                      }}
                      data-testid={TEST_IDS.offers.buy(offer.provider)}
                      disabled={isSubmitting || hasPendingRiderChanges}
                      aria-label={`${intl.formatMessage({id: 'offers.list.buy'})} ${offer.productLineName || ''}`.trim()}
                    >
                      {isSubmitting && chosenOffersIndex === offerKey ? (
                        <>
                          <CircularProgress size={20} className={classes.loadingIndicator}/>
                          <span><FormattedMessage id='offers.list.buy-selecting'/></span>
                        </>
                      ) : intl.formatMessage({id: 'offers.list.buy'})}
                    </button>
                    {hasPendingRiderChanges ? (
                      <Typography className={classes.pendingChangesMessage} role='status'>
                        <FormattedMessage id='offers.list.pendingRidersChanges' />
                      </Typography>
                    ) : null}
                  </div>
                </div>
              </div>
            </SingleOffer>
          </div>
        );
      })}
    </div>
  );
};

OffersContainer.propTypes = {
  isSubmitting: PropTypes.bool,
  hasPendingRiderChanges: PropTypes.bool,
  offers: PropTypes.object,
  submitAct: PropTypes.func,
};

const OffersPageHeader = ({contentFrameClassName, offersCount, onEditDetails, onOpenCoverage, values}) => {
  const intl = useIntl();
  const {classes, cx} = useStyles();
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const [showSelectedCoverages, setShowSelectedCoverages] = useState(false);
  const ridersList = ridersTypes
    .filter(({formikKey}) => values[formikKey])
    .map(({messageId}) => intl.formatMessage({id: messageId}));
  const selectedOptionalCoverages = ridersTypes.filter(({formikKey, id}) =>
    id !== 'locateAndExtract' && id !== 'travelThirdParty' && values[formikKey]
  ).length;
  const dateRange = [values.policyStartDate, values.policyEndDate]
    .map(date => moment(date).format('DD.MM.YY'))
    .join('–');

  return (
    <>
      <section className={classes.summaryBand} aria-label={intl.formatMessage({id: 'offers.summary.details'})}>
        <div className={cx(classes.summaryInner, contentFrameClassName)} dir='rtl'>
          <div className={classes.desktopSummaryContent}>
            <div className={classes.criteriaBar}>
              <div className={classes.criteriaTag}>
                <span className={classes.criteriaTagLabel}><FormattedMessage id='offers.summary.destination'/></span>
                <span className={classes.criteriaTagValue}>{destinationsToNames(values.targetCountries)}</span>
              </div>
              <div className={classes.criteriaTag}>
                <span className={classes.criteriaTagLabel}><FormattedMessage id='offers.summary.dates'/></span>
                <span className={classes.criteriaTagValue}>{dateRange}</span>
              </div>
              <div className={classes.criteriaTag}>
                <span className={classes.criteriaTagLabel}><FormattedMessage id='offers.summary.travelers'/></span>
                <span className={classes.criteriaTagValue}>
                  <FormattedMessage id='offers.insuredNum' values={{num: values.policyholdNum}}/>
                </span>
              </div>
              <div className={classes.criteriaTag}>
                <span className={classes.criteriaTagLabel}><FormattedMessage id='offers.summary.coverages'/></span>
                <span className={classes.criteriaTagValue}>
                  {ridersList.length ? ridersList.join(', ') : intl.formatMessage({id: 'offers.summary.baseCoverage'})}
                </span>
              </div>
            </div>
            <button className={classes.editLink} onClick={onEditDetails}>
              <FormattedMessage id='offers.summary.editDetails'/>
            </button>
          </div>

          <div className={classes.mobileSummary}>
            <div className={classes.mobileSummaryActions}>
              <button
                type='button'
                className={cx(classes.mobileSummaryButton, classes.mobileCoverageButton)}
                onClick={onOpenCoverage}
              >
                <ShieldOutlinedIcon fontSize='small'/>
                <FormattedMessage id='offers.summary.adjustCoverages'/>
                {selectedOptionalCoverages > 0 && (
                  <span className={classes.mobileCoverageBadge}>{selectedOptionalCoverages}</span>
                )}
              </button>
              <button
                type='button'
                className={classes.mobileSummaryButton}
                onClick={() => setShowRequestDetails(open => !open)}
                aria-expanded={showRequestDetails}
                aria-controls='mobile-request-details'
              >
                <EditOutlinedIcon fontSize='small'/>
                <FormattedMessage id='offers.summary.requestDetails'/>
                <span className={cx(classes.mobileSummaryChevron, showRequestDetails && classes.mobileSummaryChevronOpen)}>
                  <KeyboardArrowDownIcon fontSize='small'/>
                </span>
              </button>
            </div>
            <Collapse in={showRequestDetails}>
              <div id='mobile-request-details' className={classes.mobileCriteriaPanel}>
                <div className={classes.mobileCriteriaGrid}>
                  <div className={cx(classes.criteriaTag, classes.mobileCriteriaTag)}>
                    <span className={classes.criteriaTagLabel}><FormattedMessage id='offers.summary.destination'/></span>
                    <span className={classes.criteriaTagValue}>{destinationsToNames(values.targetCountries)}</span>
                  </div>
                  <div className={cx(classes.criteriaTag, classes.mobileCriteriaTag)}>
                    <span className={classes.criteriaTagLabel}><FormattedMessage id='offers.summary.dates'/></span>
                    <span className={classes.criteriaTagValue}>{dateRange}</span>
                  </div>
                  <div className={cx(classes.criteriaTag, classes.mobileCriteriaTag)}>
                    <span className={classes.criteriaTagLabel}><FormattedMessage id='offers.summary.travelers'/></span>
                    <span className={classes.criteriaTagValue}>
                      <FormattedMessage id='offers.insuredNum' values={{num: values.policyholdNum}}/>
                    </span>
                  </div>
                  <div className={cx(classes.criteriaTag, classes.mobileCriteriaTag, classes.mobileCoveragesTag)}>
                    <button
                      type='button'
                      className={classes.mobileCoveragesToggle}
                      onClick={() => setShowSelectedCoverages(open => !open)}
                      aria-expanded={showSelectedCoverages}
                      aria-controls='mobile-selected-coverages'
                    >
                      <span className={classes.mobileCoveragesSummary}>
                        <span className={classes.criteriaTagLabel}><FormattedMessage id='offers.summary.coverages'/></span>
                        <span className={classes.criteriaTagValue}>
                          {ridersList[0] || intl.formatMessage({id: 'offers.summary.baseCoverage'})}
                        </span>
                      </span>
                      <span className={cx(classes.mobileSummaryChevron, showSelectedCoverages && classes.mobileSummaryChevronOpen)}>
                        <KeyboardArrowDownIcon fontSize='small'/>
                      </span>
                    </button>
                    <Collapse in={showSelectedCoverages}>
                      <ul id='mobile-selected-coverages' className={classes.mobileCoveragesList}>
                        {(ridersList.length ? ridersList : [intl.formatMessage({id: 'offers.summary.baseCoverage'})]).map(coverage => (
                          <li key={coverage} className={classes.mobileCoverageListItem}>{coverage}</li>
                        ))}
                      </ul>
                    </Collapse>
                  </div>
                </div>
                <div className={classes.mobileEditRow}>
                  <button className={classes.editLink} onClick={onEditDetails}>
                    <FormattedMessage id='offers.summary.editDetails'/>
                  </button>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
      </section>
      <div className={cx(classes.pageIntro, contentFrameClassName)} dir='rtl'>
        <div className={classes.pageTitle}>
          <FormattedMessage
            id='offers.summary.title'
            values={{
              count: offersCount,
              highlight: chunks => <span className={classes.highlight}>{chunks}</span>,
            }}
          />
          <span className={classes.titleDot} aria-hidden='true'/>
        </div>
        <div className={classes.pageLead}>
          <FormattedMessage id='offers.summary.priceNote'/>
        </div>
      </div>
    </>
  );
};

OffersPageHeader.propTypes = {
  contentFrameClassName: PropTypes.string.isRequired,
  offersCount: PropTypes.number.isRequired,
  onEditDetails: PropTypes.func.isRequired,
  onOpenCoverage: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
};

export {OffersPageHeader};
export default OffersContainer;
